import { createTheme, alpha } from '@mui/material/styles';

// ─── Design Tokens ────────────────────────────────────────────────────────────
// Colors sourced from AdGPTise brand (radixtechs.com/adgptise/new2/)
export const C = {
  // Navy / dark sidebar family
  navy:      '#0d1f6e',
  navyMid:   '#1a3dbf',
  navyLight: '#2a52d4',

  // Primary blue — CTA buttons, active states, links
  warm:      '#2563eb',
  warmDark:  '#1a3dbf',
  warmLight: '#4d6ee0',

  // Secondary / info blue
  cool:      '#60a5fa',

  // Light surfaces
  offwhite:  '#F6F8FD',

  // Text — slate scale
  ink:       '#0f172a',
  inkMid:    '#1e293b',
  inkLight:  '#64748b',

  // Borders
  border:    '#dce5f7',

  // Pure
  white:     '#FFFFFF',
} as const;

// ─── MUI Theme ────────────────────────────────────────────────────────────────
export const theme = createTheme({
  palette: {
    primary: {
      main:         C.warm,
      light:        C.warmLight,
      dark:         C.warmDark,
      contrastText: C.white,
    },
    secondary: {
      main:         C.cool,
      light:        '#93c5fd',
      dark:         '#3b82f6',
      contrastText: C.white,
    },
    background: {
      default: C.offwhite,
      paper:   C.white,
    },
    text: {
      primary:   C.ink,
      secondary: C.inkMid,
      disabled:  C.inkLight,
    },
    divider: C.border,
    success: {
      main:  '#16a34a',
      light: '#bbf7d0',
      dark:  '#15803d',
    },
    warning: {
      main:  '#f97316',
      light: '#fed7aa',
      dark:  '#ea580c',
    },
    error: {
      main:  '#dc2626',
      light: '#fecaca',
      dark:  '#b91c1c',
    },
    info: {
      main:  C.cool,
      light: '#bfdbfe',
      dark:  '#2563eb',
    },
  },

  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',

    h1: { fontFamily: ' sans-serif', fontWeight: 700, fontSize: '2.25rem' },
    h2: { fontFamily: ' sans-serif', fontWeight: 700, fontSize: '1.875rem' },
    h3: { fontFamily: ' sans-serif', fontWeight: 800, fontSize: '2rem' },
    h4: { fontFamily: ' sans-serif', fontWeight: 600, fontSize: '1.25rem' },
    h5: { fontFamily: ' sans-serif', fontWeight: 600, fontSize: '1.125rem' },
    h6: { fontFamily: ' sans-serif', fontWeight: 600, fontSize: '1rem' },

    subtitle1: { fontWeight: 500, fontSize: '0.9375rem', color: C.inkMid },
    subtitle2: { fontWeight: 500, fontSize: '0.875rem', color: C.inkMid },

    body1: { fontSize: '0.9375rem', color: C.inkMid },
    body2: { fontSize: '0.875rem', color: C.inkLight },

    caption: { fontSize: '0.75rem', color: C.inkLight },

    button: {
      fontFamily: ' sans-serif',
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      letterSpacing: 0,
    },

    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: C.inkLight,
    },
  },

  shape: {
    borderRadius: 10,
  },

  shadows: [
    'none',
    '0 1px 3px rgba(13,31,110,0.06), 0 1px 2px rgba(13,31,110,0.04)',
    '0 2px 6px rgba(13,31,110,0.08), 0 1px 3px rgba(13,31,110,0.05)',
    '0 4px 12px rgba(13,31,110,0.10), 0 2px 4px rgba(13,31,110,0.06)',
    '0 6px 16px rgba(13,31,110,0.12), 0 3px 6px rgba(13,31,110,0.07)',
    '0 8px 24px rgba(13,31,110,0.14)',
    '0 10px 28px rgba(13,31,110,0.14)',
    '0 12px 32px rgba(13,31,110,0.16)',
    '0 14px 36px rgba(13,31,110,0.16)',
    '0 16px 40px rgba(13,31,110,0.18)',
    '0 18px 44px rgba(13,31,110,0.18)',
    '0 20px 48px rgba(13,31,110,0.20)',
    '0 22px 52px rgba(13,31,110,0.20)',
    '0 24px 56px rgba(13,31,110,0.22)',
    '0 26px 60px rgba(13,31,110,0.22)',
    '0 28px 64px rgba(13,31,110,0.24)',
    '0 30px 68px rgba(13,31,110,0.24)',
    '0 32px 72px rgba(13,31,110,0.26)',
    '0 34px 76px rgba(13,31,110,0.26)',
    '0 36px 80px rgba(13,31,110,0.28)',
    '0 38px 84px rgba(13,31,110,0.28)',
    '0 40px 88px rgba(13,31,110,0.30)',
    '0 42px 92px rgba(13,31,110,0.30)',
    '0 44px 96px rgba(13,31,110,0.32)',
    '0 46px 100px rgba(13,31,110,0.32)',
  ],

  components: {
    // ── AppBar ──────────────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: C.white,
          color: C.ink,
          borderBottom: `1px solid ${C.border}`,
          height: 56,
          justifyContent: 'center',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          paddingLeft: '20px',
          paddingRight: '20px',
        },
      },
    },

    // ── Drawer (sidebar) ────────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: C.navy,
          color: C.white,
          borderRight: `1px solid ${C.navyLight}`,
        },
      },
    },

    // ── Cards ────────────────────────────────────────────────────────────────
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${C.border}`,
          backgroundColor: C.white,
          transition: 'box-shadow 0.2s ease, transform 0.15s ease',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(13,31,110,0.10)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': { paddingBottom: 24 },
        },
      },
    },

    // ── Buttons ──────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: ' sans-serif',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.15s ease',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(37,99,235,0.35)',
          },
        },
        outlined: {
          borderColor: C.border,
          color: C.inkMid,
          '&:hover': {
            borderColor: C.warm,
            color: C.warm,
            backgroundColor: alpha(C.warm, 0.04),
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(C.ink, 0.06),
          },
        },
      },
    },

    // ── Text fields ──────────────────────────────────────────────────────────
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: C.white,
            '& fieldset': { borderColor: C.border },
            '&:hover fieldset': { borderColor: C.inkLight },
            '&.Mui-focused fieldset': { borderColor: C.warm, borderWidth: 1.5 },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: C.warm },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& fieldset': { borderColor: C.border },
          '&:hover fieldset': { borderColor: C.inkLight },
          '&.Mui-focused fieldset': { borderColor: C.warm, borderWidth: 1.5 },
        },
      },
    },

    // ── Chips ────────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
      },
    },

    // ── Lists (nav) ──────────────────────────────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          margin: 0,
          paddingLeft: 20,
          paddingRight: 16,
          paddingTop: 12,
          paddingBottom: 12,
          color: C.white,
          transition: 'background-color 0.15s ease, color 0.15s ease',
          borderLeft: '3px solid transparent',
          '& .MuiListItemIcon-root': {
            color: 'rgba(255,255,255,0.60)',
            minWidth: 40,
            transition: 'color 0.15s ease',
          },
          '& .MuiListItemText-primary': {
            fontSize: '0.875rem',
            fontWeight: 500,
            fontFamily: ' sans-serif',
            lineHeight: 1.4,
            opacity: 0.75,
            transition: 'opacity 0.15s ease',
            color: '#fff',
          },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.10)',
            '& .MuiListItemText-primary': { opacity: 1 },
            '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.90)' },
          },
          '&.Mui-selected': {
            backgroundColor: '#1a3dbf',
            borderLeft: `3px solid ${C.cool}`,
            paddingLeft: 17,
            '& .MuiListItemIcon-root': { color: C.cool },
            '& .MuiListItemText-primary': { fontWeight: 700, opacity: 1 },
            '&:hover': {
              backgroundColor: '#2a52d4',
            },
          },
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: { padding: '1px 0' },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: C.navyLight,
        },
      },
    },

    // ── Avatars ──────────────────────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily: ' sans-serif',
          fontWeight: 600,
        },
        colorDefault: {
          backgroundColor: C.warm,
          color: C.white,
        },
      },
    },

    // ── Badges ───────────────────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontFamily: ' sans-serif',
          fontWeight: 600,
          fontSize: '0.625rem',
        },
      },
    },

    // ── Alerts ───────────────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: '0.875rem',
        },
      },
    },

    // ── Progress ─────────────────────────────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 6,
          backgroundColor: alpha(C.warm, 0.12),
        },
        bar: {
          borderRadius: 6,
        },
      },
    },

    // ── Paper ────────────────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: C.border,
        },
      },
    },

    // ── Tables ───────────────────────────────────────────────────────────────
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: ' sans-serif',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: C.inkLight,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            backgroundColor: C.offwhite,
            borderBottom: `1px solid ${C.border}`,
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(C.warm, 0.03),
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${C.border}`,
          fontSize: '0.875rem',
          color: C.inkMid,
          padding: '12px 16px',
        },
      },
    },

    // ── Tabs ─────────────────────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: ' sans-serif',
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.875rem',
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: C.warm,
          height: 2,
        },
      },
    },

    // ── Select / Menu ─────────────────────────────────────────────────────────
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontFamily: '"Inter", sans-serif',
          '&.Mui-selected': {
            backgroundColor: alpha(C.warm, 0.08),
            '&:hover': { backgroundColor: alpha(C.warm, 0.12) },
          },
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${C.border}`,
          boxShadow: '0 8px 24px rgba(13,31,110,0.12)',
        },
      },
    },

    // ── Tooltip ──────────────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: C.navy,
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '6px 10px',
        },
        arrow: {
          color: C.navy,
        },
      },
    },

    // ── Breadcrumbs ──────────────────────────────────────────────────────────
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          color: C.inkLight,
        },
      },
    },

    // ── Switch ───────────────────────────────────────────────────────────────
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: C.warm,
            '& + .MuiSwitch-track': { backgroundColor: C.warm, opacity: 0.7 },
          },
        },
      },
    },
  },
});
